import { CSSProperties, ReactNode } from 'react';

const commonStyle = { borderRadius: '5px', padding: '0px 2px' };
const leftStyle = { borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' };
const rightStyle = { borderTopRightRadius: '0px', borderBottomRightRadius: '0px' };

type Tags = 'em' | 'primary' | 'domain' | 'secondary' | 'must' | 'exclude' | 'date' | 'section' | 'highlight';

const tagsMappings: { [K in Tags]: { style: CSSProperties; sliceIndexes: { start: number; end: number } } } = {
  em: { style: { backgroundColor: '#fff6e6', color: '#ffcb74', fontWeight: 600 }, sliceIndexes: { start: 4, end: -5 } },
  domain: {
    style: { backgroundColor: '#afc6f8', color: '#0038ff', fontWeight: 600 },
    sliceIndexes: { start: 8, end: -9 },
  },
  primary: {
    style: { backgroundColor: '#8cd4a4', color: '#21713b', fontWeight: 600 },
    sliceIndexes: { start: 9, end: -10 },
  },
  secondary: {
    style: { backgroundColor: '#ffdda3', color: '#b98804', fontWeight: 600 },
    sliceIndexes: { start: 11, end: -12 },
  },
  must: {
    style: { backgroundColor: '#e6ffe6', color: '#52c41a', fontWeight: 600 },
    sliceIndexes: { start: 6, end: -7 },
  },
  exclude: {
    style: { backgroundColor: '#ffe6e6', color: '#ff4d4f', fontWeight: 600 },
    sliceIndexes: { start: 9, end: -10 },
  },
  date: {
    style: { backgroundColor: '#e6f7ff', color: '#1890ff', fontWeight: 600 },
    sliceIndexes: { start: 6, end: -7 },
  },
  section: {
    style: { backgroundColor: '#f0e6ff', color: '#722ed1', fontWeight: 600 },
    sliceIndexes: { start: 9, end: -10 },
  },
  highlight: {
    style: { fontWeight: 700, padding: '0px' },
    sliceIndexes: { start: 11, end: -12 },
  },
};

const parseTag = (text: string, index: number, name: Tags): ReactNode => {
  const splitString = text.split(/(\s+)/);
  const length = splitString.length - 1;
  const tagMapping = tagsMappings[name];
  const { start, end } = tagMapping.sliceIndexes;

  return splitString.map((item, inIndex) => (
    <span
      key={`${index}_${name}_${item}_${inIndex}`}
      style={{
        ...commonStyle,
        ...(inIndex > 0 ? leftStyle : {}),
        ...(inIndex < length ? rightStyle : {}),
        ...tagMapping.style,
      }}
    >
      {item.slice(inIndex === 0 ? start : 0, inIndex === length ? end : item.length + 1)}
    </span>
  ));
};

export const parseHighlight = (text: string): ReactNode => {
  let isPreviousHighlight = false;
  const parts = text.split(/(<(.+)>.*?<\/\2>)/);

  return (
    <div>
      {parts.map((part, index) => {
        const parsedPart =
          part.startsWith('<em>') && part.endsWith('</em>')
            ? parseTag(part, index, 'em')
            : part.startsWith('<domain>') && part.endsWith('</domain>')
              ? parseTag(part, index, 'domain')
              : part.startsWith('<primary>') && part.endsWith('</primary>')
                ? parseTag(part, index, 'primary')
                : part.startsWith('<secondary>') && part.endsWith('</secondary>')
                  ? parseTag(part, index, 'secondary')
                  : part.startsWith('<must>') && part.endsWith('</must>')
                    ? parseTag(part, index, 'must')
                    : part.startsWith('<exclude>') && part.endsWith('</exclude>')
                      ? parseTag(part, index, 'exclude')
                      : part.startsWith('<date>') && part.endsWith('</date>')
                        ? parseTag(part, index, 'date')
                        : part.startsWith('<section>') && part.endsWith('</section>')
                          ? parseTag(part, index, 'section')
                          : part.startsWith('<highlight>') && part.endsWith('</highlight>')
                            ? parseTag(part, index, 'highlight')
                            : isPreviousHighlight
                              ? null
                              : part;

        isPreviousHighlight = typeof parsedPart === 'string' || parsedPart === null ? false : true;
        return parsedPart;
      })}
    </div>
  );
};

export const removeTags = (text: string): string => {
  return text.replace(/<(.*)>(.*?)<\/\1>/g, '$2');
};

export const simpleHighlights = (snippet: string, style: CSSModuleClasses[string]) => {
  const regex = /<em>(.*?)<\/em>/g;
  const parts = snippet.split(regex);

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className={style}>
        {part}
      </span>
    ) : (
      part
    ),
  );
};
